import { useRouter } from 'next/router';
import { MouseEventHandler, useCallback, useContext, useState } from 'react';

import { useToast } from '@vimeo/bokeh';
import { VideoFile } from '@vimeo/gctl-api/models/components/schemas/video-file/VideoFile';
import { translate } from '@vimeo/i18n-runtime';
import { useDownloadTracking } from '@vimeo/picox';
import { useVideoTagging } from '@vimeo/ui/hooks/use-video-tagging';
import { downloadFile } from '@vimeo/ui/libs/downloadFile';
import type { WebBpContext } from '@vimeo/use-tracking';
import { ViewerContext } from '@vimeo/viewer';

import type { TSelectItem } from '../types';
import { bpSelectedDownloadOption } from '../utils/analytics';
import { TRACKED_ERRORS, trackError } from '../utils/datadog';

type Props = {
  handleClick?: (file: VideoFile, isExifTagJob?: boolean) => void;
  videoID?: number;
  location?: string;
  pageName?: WebBpContext['page_name'];
  audiotrackUri?: string;
  allowAudioTracksSelector: boolean;
};

export function useOnDownloadClick({
  location,
  pageName,
  handleClick,
  videoID,
  audiotrackUri,
}: Props) {
  const [loadingButtons, setLoadingButtons] = useState<string[]>([]);

  const { shouldTagVideo, tagVideo } = useVideoTagging();
  const { trackVideoDownloadStarted } = useDownloadTracking();
  const viewer = useContext(ViewerContext);
  const path = useRouter().asPath;
  const toast = useToast();

  const showDownloadStartedToast = useCallback(() => {
    const toastId = 'download-files-download-started';
    if (!toast.isActive(toastId)) {
      toast({
        id: toastId,
        title: translate('Download started'),
        variant: 'success',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [toast]);

  const onDownloadClick = useCallback(
    (file: VideoFile, audioTrackMeta: TSelectItem): MouseEventHandler<HTMLButtonElement> =>
      (e) => {
        // always send analytics event
        bpSelectedDownloadOption(
          videoID ?? 0,
          file.quality,
          file.rendition,
          audioTrackMeta,
          pageName,
          viewer?.teamUser,
          path,
          location,
        );
        trackVideoDownloadStarted({
          videoId: videoID != null ? String(videoID) : null,
          fileQuality: file.quality ?? null,
          fileRendition: file.rendition ?? null,
        });
        handleClick && handleClick(file);
        showDownloadStartedToast();

        // tag video if conditions are met
        if (
          viewer?.apiUrl &&
          viewer?.jwt &&
          location &&
          videoID &&
          !audiotrackUri &&
          shouldTagVideo(file, location)
        ) {
          e.preventDefault(); // prevents instant download

          setLoadingButtons([...loadingButtons, file.link]);
          tagVideo(viewer.apiUrl, viewer.jwt, videoID, file.videoFileId?.toString() ?? '')
            .then((url) => {
              if (url) {
                downloadFile(url);
              } else {
                downloadFile(`${file.link}`);
              }
              setLoadingButtons([...loadingButtons.filter((e) => e !== file.link)]);
            })
            .catch((taggingError) => {
              trackError(taggingError, {
                videoID,
                audiotrackUri,
                hint: 'Failed to complete video-tagging job.',
                trackingTag: TRACKED_ERRORS.FAILED_VIDEO_TAGGING,
              });
              downloadFile(`${file.link}`);
              setLoadingButtons([...loadingButtons.filter((e) => e !== file.link)]);
            });
        }
      },
    [
      audiotrackUri,
      handleClick,
      loadingButtons,
      location,
      pageName,
      path,
      shouldTagVideo,
      showDownloadStartedToast,
      tagVideo,
      trackVideoDownloadStarted,
      videoID,
      viewer?.apiUrl,
      viewer?.jwt,
      viewer?.teamUser,
    ],
  );

  return {
    onDownloadClick,
    loadingButtons,
  };
}
